import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Play, Search, ChevronUp, ChevronDown, Filter, X, Square, Leaf, FlaskConical, Bug, ArrowLeftRight, Building } from "lucide-react";
import { authFetch } from "@/hooks/use-auth";
import ModuleHeader from "@/components/ModuleHeader";

function ProdutosTab() {
  const [page, setPage] = useState(0);
  const [limit] = useState(20);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [classe, setClasse] = useState("");
  const [statusFilter, setStatusFilter] = useState("ativo");
  const [cultura, setCultura] = useState("");
  const [ingrediente, setIngrediente] = useState("");
  const [sortBy, setSortBy] = useState("id_agrofit");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedProduto, setSelectedProduto] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [equivalentes, setEquivalentes] = useState<any>(null);
  const [equivLoading, setEquivLoading] = useState(false);

  const buildQueryString = () => {
    const params = new URLSearchParams();
    params.set("limit", limit.toString());
    params.set("offset", (page * limit).toString());
    params.set("sort_by", sortBy);
    params.set("sort_order", sortOrder);
    if (search) params.set("search", search);
    if (classe) params.set("classe", classe);
    if (statusFilter && statusFilter !== "todos") params.set("status", statusFilter);
    if (cultura) params.set("cultura", cultura);
    if (ingrediente) params.set("ingrediente", ingrediente);
    return params.toString();
  };

  const { data, isLoading } = useQuery({
    queryKey: ["/api/agrofit/produtos", page, limit, search, classe, statusFilter, cultura, ingrediente, sortBy, sortOrder],
    queryFn: () => authFetch(`/api/agrofit/produtos?${buildQueryString()}`),
  });

  const { data: filterOptions } = useQuery({
    queryKey: ["/api/agrofit/filter-options"],
    queryFn: () => authFetch("/api/agrofit/filter-options"),
  });

  const handleSort = (col: string) => {
    if (sortBy === col) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(col);
      setSortOrder("asc");
    }
    setPage(0);
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(0);
  };

  const clearFilters = () => {
    setSearch("");
    setSearchInput("");
    setClasse("");
    setStatusFilter("ativo");
    setCultura("");
    setIngrediente("");
    setPage(0);
  };

  const openDetail = async (id: number) => {
    setDetailLoading(true);
    setEquivalentes(null);
    try {
      const detail = await authFetch(`/api/agrofit/produtos/${id}`);
      setSelectedProduto(detail);
    } catch {
      setSelectedProduto(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const loadEquivalentes = async (id: number) => {
    setEquivLoading(true);
    try {
      const data = await authFetch(`/api/agrofit/equivalentes/${id}`);
      setEquivalentes(data);
    } catch {
      setEquivalentes(null);
    } finally {
      setEquivLoading(false);
    }
  };

  const SortIcon = ({ col }: { col: string }) => {
    if (sortBy !== col) return null;
    return sortOrder === "asc" ? <ChevronUp className="h-3 w-3 inline ml-1" /> : <ChevronDown className="h-3 w-3 inline ml-1" />;
  };

  const totalPages = data ? Math.ceil(data.total / limit) : 0;
  const hasFilters = search || classe || cultura || ingrediente || statusFilter !== "ativo";

  const classeColors: Record<string, string> = {
    "Herbicida": "bg-green-500/20 text-green-700 dark:text-green-400",
    "Fungicida": "bg-blue-500/20 text-blue-700 dark:text-blue-400",
    "Inseticida": "bg-red-500/20 text-red-700 dark:text-red-400",
    "Acaricida": "bg-orange-500/20 text-orange-700 dark:text-orange-400",
    "Regulador de crescimento": "bg-purple-500/20 text-purple-700 dark:text-purple-400",
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs mb-1 block">Busca geral</Label>
              <div className="flex gap-2">
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Marca, registro ou classe..."
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  data-testid="input-agrofit-search"
                />
                <Button onClick={handleSearch} size="icon" data-testid="button-agrofit-search">
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="min-w-[160px]">
              <Label className="text-xs mb-1 block">Classe</Label>
              <Select value={classe || "_all"} onValueChange={(v) => { setClasse(v === "_all" ? "" : v); setPage(0); }}>
                <SelectTrigger data-testid="select-agrofit-classe">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todas</SelectItem>
                  {filterOptions?.classes?.map((c: string) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[130px]">
              <Label className="text-xs mb-1 block">Status</Label>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
                <SelectTrigger data-testid="select-agrofit-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ativo">Ativos</SelectItem>
                  <SelectItem value="cancelado">Cancelados</SelectItem>
                  <SelectItem value="todos">Todos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[160px]">
              <Label className="text-xs mb-1 block">Cultura</Label>
              <Input
                value={cultura}
                onChange={(e) => setCultura(e.target.value)}
                placeholder="Ex: Soja, Milho..."
                onKeyDown={(e) => e.key === "Enter" && setPage(0)}
                onBlur={() => setPage(0)}
                data-testid="input-agrofit-cultura"
              />
            </div>

            <div className="min-w-[160px]">
              <Label className="text-xs mb-1 block">Ingrediente ativo</Label>
              <Input
                value={ingrediente}
                onChange={(e) => setIngrediente(e.target.value)}
                placeholder="Ex: glifosato..."
                onKeyDown={(e) => e.key === "Enter" && setPage(0)}
                onBlur={() => setPage(0)}
                data-testid="input-agrofit-ingrediente"
              />
            </div>

            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="button-agrofit-clear-filters">
                <X className="h-4 w-4 mr-1" />
                Limpar
              </Button>
            )}
          </div>
          {hasFilters && (
            <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
              <Filter className="h-3 w-3" />
              <span>{data?.total?.toLocaleString("pt-BR") || 0} produtos encontrados</span>
            </div>
          )}
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>
      ) : (
        <>
          <div className="rounded-md border overflow-auto">
            <table className="w-full text-sm" data-testid="table-agrofit-produtos">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-3 py-2 text-left cursor-pointer hover:bg-muted" onClick={() => handleSort("id_agrofit")} data-testid="th-id-agrofit">
                    ID <SortIcon col="id_agrofit" />
                  </th>
                  <th className="px-3 py-2 text-left cursor-pointer hover:bg-muted" onClick={() => handleSort("marca_comercial")} data-testid="th-marca">
                    Marca Comercial <SortIcon col="marca_comercial" />
                  </th>
                  <th className="px-3 py-2 text-left cursor-pointer hover:bg-muted" onClick={() => handleSort("classe")} data-testid="th-classe">
                    Classe <SortIcon col="classe" />
                  </th>
                  <th className="px-3 py-2 text-left" data-testid="th-ingredientes">
                    Ingredientes Ativos
                  </th>
                  <th className="px-3 py-2 text-left cursor-pointer hover:bg-muted" onClick={() => handleSort("nr_registro")} data-testid="th-registro">
                    Registro <SortIcon col="nr_registro" />
                  </th>
                  <th className="px-3 py-2 text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                {data?.data?.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">Nenhum produto encontrado</td></tr>
                ) : (
                  data?.data?.map((p: any) => (
                    <tr
                      key={p.id}
                      className="border-b hover:bg-muted/30 cursor-pointer"
                      onClick={() => openDetail(p.id)}
                      data-testid={`row-produto-${p.id}`}
                    >
                      <td className="px-3 py-2 font-mono text-xs">{p.id_agrofit}</td>
                      <td className="px-3 py-2 max-w-[300px] truncate" title={p.marca_comercial}>
                        {p.marca_comercial || "—"}
                        {p.status === "cancelado" && (
                          <Badge variant="destructive" className="ml-2 text-[10px] px-1 py-0">CANCELADO</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {p.classe ? (
                          <Badge variant="secondary" className={classeColors[p.classe] || ""}>
                            {p.classe}
                          </Badge>
                        ) : "—"}
                      </td>
                      <td className="px-3 py-2 text-xs max-w-[250px] truncate">
                        {p.ingredientes?.map((i: any) => i.nome).filter(Boolean).join(", ") || "—"}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{p.nr_registro || "—"}</td>
                      <td className="px-3 py-2 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); openDetail(p.id); }}
                          data-testid={`button-detail-${p.id}`}
                        >
                          Detalhes
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground" data-testid="text-agrofit-pagination">
              {data?.total > 0 ? `${page * limit + 1}–${Math.min((page + 1) * limit, data.total)} de ${data.total.toLocaleString("pt-BR")}` : "0 resultados"}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="button-agrofit-prev">
                Anterior
              </Button>
              <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)} data-testid="button-agrofit-next">
                Próxima
              </Button>
            </div>
          </div>
        </>
      )}

      <Dialog open={!!selectedProduto} onOpenChange={(open) => !open && setSelectedProduto(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {detailLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>
          ) : selectedProduto && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg" data-testid="text-detail-title">
                  {selectedProduto.marca_comercial || `Produto #${selectedProduto.id_agrofit}`}
                  {selectedProduto.status === "cancelado" && (
                    <Badge variant="destructive" className="ml-2 text-xs">CANCELADO</Badge>
                  )}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 mt-2">
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div><strong>ID AGROFIT:</strong> {selectedProduto.id_agrofit}</div>
                  <div><strong>Registro:</strong> {selectedProduto.nr_registro || "—"}</div>
                  <div><strong>Classe:</strong> {selectedProduto.classe || "—"}</div>
                  <div><strong>Formulação:</strong> {selectedProduto.formulacao || "—"}</div>
                  <div className="col-span-2"><strong>Class. Toxicológica:</strong> {selectedProduto.classificacao_toxicologica || "—"}</div>
                  <div className="col-span-2"><strong>Class. Ambiental:</strong> {selectedProduto.classificacao_ambiental || "—"}</div>
                  {selectedProduto.titular_registro && (
                    <div className="col-span-2 flex items-center gap-1">
                      <Building className="h-3.5 w-3.5 text-muted-foreground" />
                      <strong>Titular:</strong> {selectedProduto.titular_registro}
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadEquivalentes(selectedProduto.id)}
                    disabled={equivLoading}
                    data-testid="button-equivalentes"
                  >
                    {equivLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ArrowLeftRight className="h-4 w-4 mr-1" />}
                    Ver Equivalentes
                  </Button>
                </div>

                {equivalentes && (
                  <div>
                    <h4 className="font-semibold text-sm mb-2 flex items-center gap-1">
                      <ArrowLeftRight className="h-4 w-4" />
                      Produtos Equivalentes ({equivalentes.total})
                    </h4>
                    {equivalentes.total === 0 ? (
                      <p className="text-xs text-muted-foreground">Nenhum produto equivalente encontrado com os mesmos ingredientes ativos e classe.</p>
                    ) : (
                      <div className="rounded border overflow-auto max-h-[180px]">
                        <table className="w-full text-xs">
                          <thead className="sticky top-0">
                            <tr className="bg-muted/50 border-b">
                              <th className="px-2 py-1 text-left">Marca</th>
                              <th className="px-2 py-1 text-left">Formulação</th>
                              <th className="px-2 py-1 text-left">Titular</th>
                              <th className="px-2 py-1 text-left">Registro</th>
                            </tr>
                          </thead>
                          <tbody>
                            {equivalentes.equivalentes.map((eq: any) => (
                              <tr key={eq.id} className="border-b hover:bg-muted/30 cursor-pointer" onClick={() => openDetail(eq.id)}>
                                <td className="px-2 py-1 font-medium">{eq.marca_comercial || "—"}</td>
                                <td className="px-2 py-1">{eq.formulacao || "—"}</td>
                                <td className="px-2 py-1 text-muted-foreground">{eq.titular_registro || "—"}</td>
                                <td className="px-2 py-1 font-mono">{eq.nr_registro || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {selectedProduto.ingredientes?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm mb-2 flex items-center gap-1"><FlaskConical className="h-4 w-4" /> Ingredientes Ativos</h4>
                    <div className="rounded border overflow-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-muted/50 border-b">
                            <th className="px-2 py-1 text-left">Nome</th>
                            <th className="px-2 py-1 text-left">Grupo Químico</th>
                            <th className="px-2 py-1 text-right">Concentração</th>
                            <th className="px-2 py-1 text-right">%</th>
                            {selectedProduto.ingredientes.some((i: any) => i.formula_bruta) && (
                              <th className="px-2 py-1 text-left">Fórmula</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {selectedProduto.ingredientes.map((ing: any, i: number) => (
                            <tr key={i} className="border-b">
                              <td className="px-2 py-1">{ing.nome || "—"}</td>
                              <td className="px-2 py-1">{ing.grupo_quimico || "—"}</td>
                              <td className="px-2 py-1 text-right">{ing.quantidade ? `${ing.quantidade} ${ing.unidade_medida || ""}` : "—"}</td>
                              <td className="px-2 py-1 text-right">{ing.percentual || "—"}%</td>
                              {selectedProduto.ingredientes.some((ii: any) => ii.formula_bruta) && (
                                <td className="px-2 py-1 font-mono text-[10px]">{ing.formula_bruta || "—"}</td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {selectedProduto.indicacoes?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm mb-2 flex items-center gap-1"><Bug className="h-4 w-4" /> Indicações de Uso ({selectedProduto.indicacoes.length})</h4>
                    <div className="rounded border overflow-auto max-h-[250px]">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0">
                          <tr className="bg-muted/50 border-b">
                            <th className="px-2 py-1 text-left">Cultura</th>
                            <th className="px-2 py-1 text-left">Praga</th>
                            <th className="px-2 py-1 text-left">Nome Comum</th>
                            <th className="px-2 py-1 text-right">Dose</th>
                            <th className="px-2 py-1 text-left">Unidade</th>
                            <th className="px-2 py-1 text-right">Intervalo Seg.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedProduto.indicacoes.map((ind: any, i: number) => (
                            <tr key={i} className="border-b">
                              <td className="px-2 py-1">{ind.cultura || "—"}</td>
                              <td className="px-2 py-1 italic">{ind.praga || "—"}</td>
                              <td className="px-2 py-1">{ind.nome_comum || "—"}</td>
                              <td className="px-2 py-1 text-right whitespace-nowrap">
                                {ind.dose_min || ind.dose_max
                                  ? `${ind.dose_min || ""}${ind.dose_max && ind.dose_min ? " - " : ""}${ind.dose_max || ""}`
                                  : "—"}
                              </td>
                              <td className="px-2 py-1 text-[10px]">{ind.unidade_dose || "—"}</td>
                              <td className="px-2 py-1 text-right">{ind.intervalo_seguranca || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ImportTab() {
  const [idInicio, setIdInicio] = useState("5000");
  const [idFim, setIdFim] = useState("23000");
  const { toast } = useToast();
  const qc = useQueryClient();

  const { data: progress, isLoading: progressLoading } = useQuery({
    queryKey: ["/api/agrofit/import-progress"],
    queryFn: () => authFetch("/api/agrofit/import-progress"),
    refetchInterval: 3000,
  });

  const { data: history } = useQuery({
    queryKey: ["/api/agrofit/import-history"],
    queryFn: () => authFetch("/api/agrofit/import-history"),
  });

  const { data: stats } = useQuery({
    queryKey: ["/api/agrofit/stats"],
    queryFn: () => authFetch("/api/agrofit/stats"),
    refetchInterval: progress?.running ? 10000 : false,
  });

  const importMutation = useMutation({
    mutationFn: () =>
      authFetch("/api/agrofit/import", {
        method: "POST",
        body: JSON.stringify({ id_inicio: parseInt(idInicio), id_fim: parseInt(idFim) }),
      }),
    onSuccess: (data: any) => {
      toast({ title: "Importação iniciada", description: data.message });
      qc.invalidateQueries({ queryKey: ["/api/agrofit/import-progress"] });
    },
    onError: (err: Error) => {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    },
  });

  const stopMutation = useMutation({
    mutationFn: () => authFetch("/api/agrofit/import/stop", { method: "POST" }),
    onSuccess: () => {
      toast({ title: "Importação parada" });
      qc.invalidateQueries({ queryKey: ["/api/agrofit/import-progress"] });
    },
  });

  const progressPercent = progress?.running && progress.idFim > progress.idInicio
    ? Math.round(((progress.currentId - progress.idInicio) / (progress.idFim - progress.idInicio)) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6">
              <div className="text-3xl font-bold text-primary" data-testid="text-total-produtos">{stats.totalProdutos?.toLocaleString("pt-BR") || 0}</div>
              <div className="text-sm text-muted-foreground">Produtos</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-3xl font-bold" data-testid="text-total-ingredientes">{stats.totalIngredientesAtivos || 0}</div>
              <div className="text-sm text-muted-foreground">Ingredientes Ativos</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-3xl font-bold" data-testid="text-total-culturas">{stats.totalCulturas || 0}</div>
              <div className="text-sm text-muted-foreground">Culturas</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-sm space-y-1" data-testid="list-classes">
                {stats.porClasse?.slice(0, 5).map((c: any) => (
                  <div key={c.classe} className="flex justify-between">
                    <span className="text-xs">{c.classe}</span>
                    <Badge variant="secondary" className="text-xs">{parseInt(c.total).toLocaleString("pt-BR")}</Badge>
                  </div>
                ))}
              </div>
              <div className="text-sm text-muted-foreground mt-1">Por Classe</div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Play className="h-5 w-5" />
            Importar do AGROFIT
          </CardTitle>
        </CardHeader>
        <CardContent>
          {progress?.running ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Processando ID <strong>{progress.currentId}</strong> de {progress.idInicio} a {progress.idFim}</span>
              </div>
              <Progress value={progressPercent} className="h-2" />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{progressPercent}% concluído</span>
                <span>{progress.produtosEncontrados} produtos encontrados | {progress.erros} erros</span>
              </div>
              {progress.ultimoProduto && (
                <div className="text-xs text-muted-foreground">
                  Último produto: {progress.ultimoProduto}
                </div>
              )}
              <Button variant="destructive" size="sm" onClick={() => stopMutation.mutate()} data-testid="button-stop-import">
                <Square className="h-4 w-4 mr-2" />
                Parar Importação
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex gap-4 items-end">
                <div>
                  <Label className="text-xs">ID Início</Label>
                  <Input
                    type="number"
                    value={idInicio}
                    onChange={(e) => setIdInicio(e.target.value)}
                    className="w-32"
                    data-testid="input-id-inicio"
                  />
                </div>
                <div>
                  <Label className="text-xs">ID Fim</Label>
                  <Input
                    type="number"
                    value={idFim}
                    onChange={(e) => setIdFim(e.target.value)}
                    className="w-32"
                    data-testid="input-id-fim"
                  />
                </div>
                <Button
                  onClick={() => importMutation.mutate()}
                  disabled={importMutation.isPending}
                  data-testid="button-start-agrofit-import"
                >
                  {importMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                  Iniciar Importação
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Produtos válidos estão geralmente entre IDs 5.000 e 23.000. Cada produto leva ~1.2s para processar.
                Range de 18.000 IDs ≈ 6 horas.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {history?.data?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Histórico de Importações</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded border overflow-auto">
              <table className="w-full text-sm" data-testid="table-agrofit-history">
                <thead>
                  <tr className="bg-muted/50 border-b">
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Range IDs</th>
                    <th className="px-3 py-2 text-right">Produtos</th>
                    <th className="px-3 py-2 text-right">Erros</th>
                    <th className="px-3 py-2 text-left">Início</th>
                    <th className="px-3 py-2 text-right">Duração</th>
                  </tr>
                </thead>
                <tbody>
                  {history.data.map((h: any) => (
                    <tr key={h.id} className="border-b">
                      <td className="px-3 py-2">
                        <Badge variant={h.status === "completed" ? "default" : h.status === "running" ? "secondary" : "destructive"}>
                          {h.status === "completed" ? "Concluída" : h.status === "running" ? "Em andamento" : "Erro"}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{h.id_inicio} — {h.id_fim}</td>
                      <td className="px-3 py-2 text-right">{h.total_produtos?.toLocaleString("pt-BR") || 0}</td>
                      <td className="px-3 py-2 text-right">{h.total_erros || 0}</td>
                      <td className="px-3 py-2 text-xs">{h.iniciado_em ? new Date(h.iniciado_em).toLocaleString("pt-BR") : "—"}</td>
                      <td className="px-3 py-2 text-right text-xs">
                        {h.duracao_segundos ? `${Math.floor(h.duracao_segundos / 60)}min ${h.duracao_segundos % 60}s` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function AgrofitPage() {
  const qc = useQueryClient();
  const { data: stats, isFetching: statsFetching } = useQuery({
    queryKey: ["/api/agrofit/stats"],
    queryFn: () => authFetch("/api/agrofit/stats"),
  });

  return (
    <div data-testid="page-agrofit">
      <ModuleHeader
        icon={Leaf}
        iconColor="text-green-600"
        title="AGROFIT"
        description="Agrotóxicos Fitossanitários — Ministério da Agricultura"
        recordCount={stats?.totalProdutos}
        recordLabel="produtos"
        lastUpdate={stats?.ultima_importacao}
        onRefresh={() => qc.invalidateQueries({ queryKey: ["/api/agrofit/stats"] })}
        refreshing={statsFetching}
        docsHref="/docs"
      />

      <Tabs defaultValue="produtos" data-testid="tabs-agrofit">
        <TabsList>
          <TabsTrigger value="produtos" data-testid="tab-agrofit-produtos">Produtos</TabsTrigger>
          <TabsTrigger value="importacao" data-testid="tab-agrofit-importacao">Importação</TabsTrigger>
        </TabsList>
        <TabsContent value="produtos" className="mt-4">
          <ProdutosTab />
        </TabsContent>
        <TabsContent value="importacao" className="mt-4">
          <ImportTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
